import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

/**
 * Per-user UI/feature preferences, backed by `/api/me/preferences` (account-level, so they follow
 * the user across browsers, unlike the app's other localStorage prefs).
 *
 *  - `adAttributionEnabled`, show the CTWA "Ad performance" + Leads features (opt-in, free).
 *  - `onboardingCompleted` , whether the one-time welcome has been completed/dismissed.
 *  - `worksWithATeam` / `usesSeveralNumbers` / `workspaceSetupCompleted`, how much of the
 *    workspace administration this account needs; see `useWorkspaceShape`.
 *
 * The backend always returns a complete, defaults-merged object; these are the client-side
 * fallbacks used before the first load resolves and after sign-out.
 */
const DEFAULT_PREFERENCES = {
  adAttributionEnabled: false,
  onboardingCompleted: false,
  worksWithATeam: false,
  usesSeveralNumbers: false,
  workspaceSetupCompleted: false,
};

const PreferencesContext = createContext();

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

export const PreferencesProvider = ({ children }) => {
  const { user, isAuthenticated, authenticatedFetch } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);

  // authenticatedFetch is recreated each render; hold it in a ref so effects/callbacks don't
  // re-run on every render (mirrors useDriftCheck's hardening).
  const fetchRef = useRef(authenticatedFetch);
  fetchRef.current = authenticatedFetch;

  const userId = user?.id;
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setPreferences(DEFAULT_PREFERENCES);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchRef.current('/api/me/preferences')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setPreferences({ ...DEFAULT_PREFERENCES, ...data });
        }
      })
      .catch(() => { /* keep defaults; non-fatal */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, userId]);

  // Merge a partial patch, optimistically, then reconcile with the server's response.
  const updatePreferences = useCallback(async (patch) => {
    setPreferences((prev) => ({ ...prev, ...patch }));
    try {
      const res = await fetchRef.current('/api/me/preferences', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences({ ...DEFAULT_PREFERENCES, ...data });
      }
    } catch {
      /* keep the optimistic value; a later load reconciles */
    }
  }, []);

  const setPreference = useCallback((key, value) => updatePreferences({ [key]: value }), [updatePreferences]);

  return (
    <PreferencesContext.Provider value={{ preferences, loading, setPreference, updatePreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};
